// The staff queue for self-serve onboarding (/admin/onboarding, platform admins).
//
// Self-serve onboarding, Phase 3 (spec §9.1 in Cloudflare-Worker,
// https://github.com/Digital-Mozarts/Cloudflare-Worker, branch feature/self-serve-onboarding):
// pages whose proposal has a hard flag, and applies that kept failing, wait here. Staff see why,
// what was proposed and the page's history, then clear it (back to the customer), approve it
// (staff apply it themselves, the only way route handling goes live), or reject it with a note
// the customer sees. A page with route handling has no Clear. Served by /api/admin/onboarding.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const QUEUED = new Set(['needs_staff', 'failed']);
const STATUS_LABELS = { needs_staff: 'Needs review', failed: 'Apply failed' };
const OUTCOMES = { approve: 'applied', clear: 'handed back to the customer', reject: 'sent back to the customer' };
// A proposal with route handling can't be cleared: only staff can apply it, so no customer could.
const staffOnly = (site) => !!site.proposal && (site.proposal.extensions || []).some((e) => e.approval === 'staff');
const messageOf = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback;

function detectedLine(detected) {
    if (!detected) return null;
    const parts = [detected.platform !== 'none' ? detected.platform : null, detected.framework, detected.renderMode].filter(Boolean);
    return `Detected: ${parts.join(' · ')}${detected.router ? ' · client-side router' : ''}`;
}

const AdminOnboardingQueue = ({ app_url }) => {
    const [sites, setSites] = useState(null);
    const [notes, setNotes] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        axios.get(`${app_url}/api/admin/onboarding/flagged`, { withCredentials: true })
            .then((response) => { if (!cancelled) setSites(response.data.sites || []); })
            .catch((err) => {
                if (cancelled) return;
                setSites([]);
                setError(messageOf(err, 'Could not load the queue.'));
            });
        return () => { cancelled = true; };
    }, [app_url]);

    const decide = async (site, decision) => {
        setBusyId(site.id);
        setMessage('');
        setError('');
        try {
            const response = await axios.post(
                `${app_url}/api/admin/onboarding/sites/${site.id}/review`,
                { decision, note: notes[site.id] || '' },
                { withCredentials: true },
            );
            const updated = response.data.site;
            setSites((list) => (QUEUED.has(updated.status)
                ? list.map((s) => (s.id === updated.id ? updated : s))
                : list.filter((s) => s.id !== updated.id)));
            setMessage(`${site.shop}: ${OUTCOMES[decision]}.`);
        } catch (err) {
            setError(messageOf(err, 'Could not save the review.'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="main-container" style={{ padding: '24px 32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#101828' }}>Onboarding queue</h2>
            <p className="text-muted" style={{ fontSize: 14 }}>
                Self-serve pages that need our team: proposals with a hard flag, and approvals that could not be applied.
            </p>
            {message && <p className="text-success" aria-live="polite">{message}</p>}
            {error && <p className="text-danger" aria-live="polite">{error}</p>}
            {sites && sites.length === 0 && <p>No pages are waiting for review.</p>}

            {(sites || []).map((site) => (
                <article key={site.id} className="card mb-3" style={{ padding: 16 }} aria-label={`${site.shop} ${site.landingPage}`}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <strong>{site.shop}</strong>
                        <a href={site.landingPage} target="_blank" rel="noreferrer">{site.landingPage}</a>
                        <span className="badge bg-warning text-dark">{STATUS_LABELS[site.status] || site.status}</span>
                    </div>
                    {site.detected && <p className="text-muted small mb-2">{detectedLine(site.detected)}</p>}

                    {site.proposal && (
                        <>
                            <p className="mb-1"><strong>Why it is here</strong></p>
                            <ul>
                                {site.proposal.flags.map((flag) => (
                                    <li key={flag.id}>
                                        <span className={flag.level === 'hard' ? 'text-danger' : 'text-muted'}>[{flag.level}]</span>{' '}
                                        <span>{flag.text}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mb-1"><strong>Proposed</strong></p>
                            <ul>
                                {site.proposal.extensions.length === 0 && <li>Nothing extra</li>}
                                {site.proposal.extensions.map((extension) => (
                                    <li key={extension.id}>
                                        <code>{extension.id}</code>{' '}
                                        <span className="text-muted">({extension.approval === 'staff' ? 'staff only' : 'customer can approve'})</span>
                                    </li>
                                ))}
                            </ul>
                            <ul className="text-muted small">
                                {site.proposal.evidence.map((item) => <li key={item.about}>{item.text}</li>)}
                            </ul>
                        </>
                    )}
                    {site.staffReview && (
                        <p className="small mb-2">
                            {site.staffReview.decision === 'reject' ? `Sent back: ${site.staffReview.note}` : `Last review: ${site.staffReview.decision}`}
                        </p>
                    )}

                    <details className="mb-2">
                        <summary>History ({site.history.length})</summary>
                        <ul className="small">
                            {site.history.map((step, i) => (
                                <li key={i}>
                                    {new Date(step.at).toLocaleString()}: {step.action}
                                    {step.to ? ` → ${step.to}` : ''}
                                    {step.actor && step.actor.email ? ` (${step.actor.email})` : ''}
                                </li>
                            ))}
                        </ul>
                    </details>

                    <label htmlFor={`onboarding-note-${site.id}`} className="small">Note for {site.shop}</label>
                    <textarea
                        id={`onboarding-note-${site.id}`}
                        className="form-control mb-2"
                        rows={2}
                        value={notes[site.id] || ''}
                        onChange={(e) => setNotes((all) => ({ ...all, [site.id]: e.target.value }))}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                        {!staffOnly(site) && (
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busyId === site.id} onClick={() => decide(site, 'clear')}>Clear</button>
                        )}
                        <button type="button" className="btn btn-primary btn-sm" disabled={busyId === site.id} onClick={() => decide(site, 'approve')}>Approve</button>
                        <button type="button" className="btn btn-outline-danger btn-sm" disabled={busyId === site.id} onClick={() => decide(site, 'reject')}>Reject</button>
                    </div>
                </article>
            ))}
        </div>
    );
};

export default AdminOnboardingQueue;
